import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Check for admin token first
  const adminToken = localStorage.getItem('admin_token');
  if (adminToken) {
    config.headers.Authorization = `Bearer ${adminToken}`;
    return config;
  }
  
  // Check for guest token
  const guestToken = localStorage.getItem('guest_token');
  if (guestToken) {
    config.headers.Authorization = `Bearer ${guestToken}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin') {
        window.location.href = '/admin';
      }
    }
    return Promise.reject(error);
  }
);

// ===================== WEDDING PHOTOS API =====================

export const getAuthToken = () => localStorage.getItem('admin_token') || localStorage.getItem('guest_token');

export const getWeddingPhotos = () => api.get('/photos');

export const uploadWeddingPhoto = (file, uploaderName) => {
  const formData = new FormData();
  formData.append('file', file);
  if (uploaderName) formData.append('uploader_name', uploaderName);
  return api.post('/photos/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 900000,
  });
};

export const likeWeddingPhoto = (photoId) => api.post(`/photos/${photoId}/like`);

export const photoFileUrl = (photoId, { thumb = false, download = false } = {}) =>
  `${API_BASE}/photos/file/${photoId}?${thumb ? 'thumb=1&' : ''}${download ? 'download=1&' : ''}auth=${getAuthToken()}`;

export const getAdminPhotos = () => api.get('/admin/photos');

export const updatePhotoSettings = (autoPublish) => api.put('/admin/photos/settings', { auto_publish: autoPublish });

export const updatePhotoStatus = (photoId, status) => api.put(`/admin/photos/${photoId}`, { status });

export const deleteWeddingPhoto = (photoId) => api.delete(`/admin/photos/${photoId}`);

export const bulkPhotoAction = (ids, action) => api.post('/admin/photos/bulk', { ids, action });

export const getPhotosCount = () => api.get('/photos/count');

export const albumAccess = (email) => api.post('/photos/album-access', { email });

export const getAlbumEmails = () => api.get('/admin/album-emails');

export const addAlbumEmail = (email) => api.post('/admin/album-emails', { email });

export const deleteAlbumEmail = (entryId) => api.delete(`/admin/album-emails/${entryId}`);

export const getPhotosTrash = () => api.get('/admin/photos/trash');

export const restoreWeddingPhoto = (photoId) => api.post(`/admin/photos/${photoId}/restore`);

export const adminPhotosZipUrl = (ids) =>
  `${API_BASE}/admin/photos/download?auth=${localStorage.getItem('admin_token')}${ids && ids.length ? `&ids=${ids.join(',')}` : ''}`;

export const photosQrUrl = (style) =>
  `${API_BASE}/admin/photos/qrcode?style=${style}&target_url=${encodeURIComponent(`${window.location.origin}/photos`)}&auth=${localStorage.getItem('admin_token')}`;

// ===================== PUBLIC API =====================

export const getConfig = () => api.get('/config');

export const createRSVP = (data) => api.post('/rsvp', data);

export const getRSVP = (rsvpId) => api.get(`/rsvp/${rsvpId}`);

export const updateRSVPPublic = (rsvpId, data) => api.put(`/rsvp/${rsvpId}`, data);

export const createAccommodation = (data) => api.post('/accommodation', data);

export const getAccommodation = (rsvpId) => api.get(`/accommodation/${rsvpId}`);

export const createPayment = (data) => api.post('/payment', data);

export const getPayment = (rsvpId) => api.get(`/payment/${rsvpId}`);

export const getPaymentInfo = () => api.get('/payment-info');

// ===================== ADMIN API =====================

export const adminRegister = (data) => api.post('/admin/register', data);

export const adminLogin = (data) => api.post('/admin/login', data);

export const getAdminMe = () => api.get('/admin/me');

export const getDashboard = () => api.get('/admin/dashboard');

export const getAllRSVPs = () => api.get('/admin/rsvps');

export const updateRSVP = (rsvpId, data) => api.put(`/admin/rsvp/${rsvpId}`, data);

export const deleteRSVP = (rsvpId) => api.delete(`/admin/rsvp/${rsvpId}`);

export const sendReminder = (rsvpId) => api.post(`/admin/rsvp/${rsvpId}/send-reminder`);

export const confirmReception = (rsvpId) => api.post(`/admin/rsvp/${rsvpId}/confirm-reception`);

export const getAllAccommodations = () => api.get('/admin/accommodations');

export const updateAccommodation = (accId, data) => api.put(`/admin/accommodation/${accId}`, data);
export const deleteAccommodation = (accId) => api.delete(`/admin/accommodation/${accId}`);
export const createAccommodationAdmin = (data) => api.post('/admin/accommodation', data);

export const confirmAccommodation = (accId) => api.post(`/admin/accommodation/${accId}/confirm`);

export const getAllPayments = () => api.get('/admin/payments');

export const validatePayment = (paymentId) => api.put(`/admin/payment/${paymentId}/validate`);

export const refundPayment = (paymentId) => api.put(`/admin/payment/${paymentId}/refund`);

export const getAdminConfig = () => api.get('/admin/config');

export const updateAdminConfig = (data) => api.put('/admin/config', data);

export const exportCSV = () => api.get('/admin/export');

// ===================== GUEST SPACE API =====================

export const guestLogin = (email) => api.post('/guest/login', { email });

export const guestVerify = (email, code) => api.post('/guest/verify', { email, code });

export const getGuestMe = () => api.get('/guest/me');

export const getGuestQuestions = () => api.get('/guest/questions');

export const createGuestQuestion = (rsvpId, question) => api.post('/guest/questions', { rsvp_id: rsvpId, question });

// ===================== FAQ API =====================

export const getFAQ = () => api.get('/faq');

// ===================== ADMIN QUESTIONS API =====================

export const getAllQuestions = () => api.get('/admin/questions');

export const answerQuestion = (questionId, answer) => api.put(`/admin/questions/${questionId}/answer`, { answer });

export const deleteQuestion = (questionId) => api.delete(`/admin/questions/${questionId}`);

// ===================== ADMIN FAQ API =====================

export const getAdminFAQ = () => api.get('/admin/faq');

export const createFAQ = (data) => api.post('/admin/faq', data);

export const updateFAQ = (faqId, data) => api.put(`/admin/faq/${faqId}`, data);

export const deleteFAQ = (faqId) => api.delete(`/admin/faq/${faqId}`);

// ===================== GUESTBOOK (LIVRE D'OR) API =====================

export const getGuestbookMessages = () => api.get('/guestbook');

export const addGuestbookMessage = (data) => api.post('/guestbook', data);

export const getAdminGuestbook = () => api.get('/admin/guestbook');

export const updateGuestbookMessage = (messageId, data) => api.put(`/admin/guestbook/${messageId}`, data);

export const deleteGuestbookMessage = (messageId) => api.delete(`/admin/guestbook/${messageId}`);

// ===================== BLOG API =====================

export const getBlogPosts = () => api.get('/blog');

export const getBlogPost = (postId) => api.get(`/blog/${postId}`);

// ===================== ADMIN BLOG API =====================

export const getAdminBlogPosts = () => api.get('/admin/blog');

export const createBlogPost = (data) => api.post('/admin/blog', data);

export const updateBlogPost = (postId, data) => api.put(`/admin/blog/${postId}`, data);

export const deleteBlogPost = (postId) => api.delete(`/admin/blog/${postId}`);

// ===================== FILE UPLOAD API =====================

export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// ===================== ADMIN REMINDERS API =====================

export const getReminderConfig = () => api.get('/admin/reminders/config');

export const updateReminderConfig = (data) => api.put('/admin/reminders/config', data);

export const getPendingReminders = () => api.get('/admin/reminders/pending');

export const sendAllReminders = () => api.post('/admin/reminders/send-all');

export const resetReminderCount = (rsvpId) => api.post(`/admin/reminders/reset/${rsvpId}`);

// ===================== PUSH NOTIFICATIONS API =====================

export const getVapidPublicKey = () => api.get('/push/vapid-public-key');

export const subscribePush = (data) => api.post('/push/subscribe', data);

export const unsubscribePush = (data) => api.post('/push/unsubscribe', data);

export const getPushStats = () => api.get('/admin/push/stats');

export const sendTestPush = () => api.post('/admin/push/test');

// ===================== PROGRAMME API =====================

export const getProgramme = () => api.get('/programme');

export const getAdminProgramme = () => api.get('/admin/programme');

export const updateProgramme = (data) => api.put('/admin/programme', data);

// ===================== MAILJET DELIVERABILITY API =====================

export const getMailjetStats = () => api.get('/admin/mailjet/stats');

export default api;
